import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { ApiError, invitePreviewQuery, useAcceptInvite } from "@bridewell/api-client";
import type { Session } from "@/api/auth";
import { sessionQuery } from "@/api/session";
import { Button } from "@/components/ui/button";
import { ButtonLink } from "@/components/ui/button-link";
import { Field, Input } from "@/components/ui/field";
import { Spinner } from "@/components/ui/misc";
import { AuthLayout } from "@/features/auth/auth-layout";
import { noindexHead } from "@/features/marketing/seo";
import { label, t } from "@/i18n";
import { dateLong } from "@/lib/format";
import { errorMessage } from "@/lib/error-reporting";

export const Route = createFileRoute("/join/$token")({
  head: () => noindexHead(t("marketing.seo.join")),
  loader: ({ context }) => context.queryClient.ensureQueryData(sessionQuery),
  component: JoinPage,
});

/** Invite landing: works signed-out (preview) and signed-in (accept). Link = access. */
function JoinPage() {
  const session = useQuery(sessionQuery).data;
  // Drafts and mutation state belong to this session too, not the next account.
  return <JoinSessionPage key={session ? `${session.user.id}:${session.session.id}` : "signed-out"} session={session ?? null} />;
}

function JoinSessionPage({ session }: { session: Session | null }) {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const preview = useQuery(invitePreviewQuery(token));
  const [displayName, setDisplayName] = useState(session?.user.name ?? "");
  const acceptInvite = useAcceptInvite(token);
  const submitting = useRef(false);
  // A seat can be consumed before its reply is lost. Only an explicit 4xx refusal is safe to resubmit.
  const uncertain = acceptInvite.isError && !(acceptInvite.error instanceof ApiError && acceptInvite.error.status < 500);
  const accept = {
    ...acceptInvite,
    mutate: () => {
      if (!session || submitting.current || uncertain) return;
      submitting.current = true;
      acceptInvite.mutate(displayName, { onSuccess: ({ weddingId }) => {
        // A late accept belongs to the account/session that submitted it, not
        // to whoever signed in while the request was in flight.
        const current = qc.getQueryData(sessionQuery.queryKey);
        if (current?.user.id !== session.user.id || current?.session.id !== session.session.id) return;
        void navigate({ to: "/app/w/$weddingId", params: { weddingId } });
      }, onSettled: () => { submitting.current = false; } });
    },
  };
  const here = `/join/${token}`;

  if (preview.isPending) return <AuthLayout title={t("join.title")}><Spinner /></AuthLayout>;
  if (preview.isError || !preview.data?.valid) {
    return (
      <AuthLayout title={t("join.invalidTitle")} subtitle={t("join.invalidBody")}>
        <ButtonLink to="/app" size="lg">{t("join.goToWeddings")}</ButtonLink>
      </AuthLayout>
    );
  }
  const p = preview.data;
  return (
    <AuthLayout title={t("join.joinCouple", { couple: p.coupleTitle })} subtitle={`${dateLong(p.weddingDate)} · ${t("join.joiningAs", { role: label("role", p.role) })}`}>
      {p.seatBound && p.displayName && <p className="mb-4 break-words text-sm text-text-secondary">{t("join.seatFor", { name: p.displayName })}</p>}
      {session ? (
        <form onSubmit={(e) => { e.preventDefault(); accept.mutate(); }} className="flex flex-col gap-4">
          <Field label={t("auth.yourName")} hint={t("join.nameHint")}><Input required maxLength={80} value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></Field>
          {accept.isError && <p role="alert" className="text-sm text-danger">{errorMessage(accept.error)}</p>}
          {uncertain && <><p className="text-sm text-text-secondary">{t("join.uncertain")}</p><ButtonLink to="/app" variant="secondary">{t("join.goToWeddings")}</ButtonLink></>}
          <Button type="submit" size="lg" loading={accept.isPending} disabled={uncertain}>{t("join.cta")}</Button>
        </form>
      ) : (
        <div className="flex flex-col gap-3">
          <ButtonLink to="/signup" search={{ redirect: here }} size="lg">{t("auth.signUp")}</ButtonLink>
          <ButtonLink to="/login" search={{ redirect: here }} size="lg" variant="secondary">{t("auth.signIn")}</ButtonLink>
        </div>
      )}
    </AuthLayout>
  );
}
